import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'

import { ForecastProductListParamsType } from 'features/SCMS/@types/forecastFilterTypes'
import {
  getSkuReviewProductList,
  markProductReviewAPI,
  getProductDetail,
  getProductOverview,
  getProductTrend,
  getProductDailyDistribution,
  getProductHubSummary,
  getProductHubList,
  getSkuReviewHubDistribution,
} from '../../services/skuDetailsReview'
import type {
  SkuReviewProductListParamsType,
  MarkReviewPayloadType,
  ProductOverviewParamsType,
  ProductTrendParamsType,
  DailyDistributionParamsType,
  HubListParamsType,
  SkuReviewHubDistributionParamsType,
} from '../../@types/dpTypes'
import { getSkuReviewSummary } from '../../services/demandPlanning'

const sliceName = 'skuDetailsReview'

export const fetchSkuReviewProductList = createAsyncThunk(
  `${sliceName}/fetchSkuReviewProductList`,
  async (
    { params }: { params?: SkuReviewProductListParamsType; append?: boolean },
    { rejectWithValue },
  ) => {
    try {
      let order_by = ''

      if (params?.order_by === 'average_gap_pct') {
        order_by = 'avg_gap_pct'
      }
      if (params?.order_by === 'average_gap') {
        order_by = 'avg_gap'
      }

      const response = await getSkuReviewProductList({ ...params, order_by })
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const markProductReview = createAsyncThunk(
  `${sliceName}/markProductReview`,
  async (
    { payload, successCallback }: { payload: MarkReviewPayloadType; successCallback: () => void },
    { rejectWithValue },
  ) => {
    try {
      const response = await markProductReviewAPI(payload)
      successCallback()
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductDetail = createAsyncThunk(
  `${sliceName}/fetchProductDetail`,
  async (productId: number, { rejectWithValue }) => {
    try {
      const response = await getProductDetail(productId)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductOverview = createAsyncThunk(
  `${sliceName}/fetchProductOverview`,
  async (
    { productId, params }: { productId: number; params?: ProductOverviewParamsType },
    { rejectWithValue },
  ) => {
    try {
      const response = await getProductOverview(productId, params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductTrend = createAsyncThunk(
  `${sliceName}/fetchProductTrend`,
  async (
    { productId, params }: { productId: number; params?: ProductTrendParamsType },
    { rejectWithValue },
  ) => {
    try {
      const response = await getProductTrend(productId, params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductDailyDistribution = createAsyncThunk(
  `${sliceName}/fetchProductDailyDistribution`,
  async (
    {
      productId,
      params,
    }: {
      productId: number
      params?: DailyDistributionParamsType
      requestKey: string
      monthKey: string
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getProductDailyDistribution(productId, params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductHubSummary = createAsyncThunk(
  `${sliceName}/fetchProductHubSummary`,
  async (productId: number, { rejectWithValue }) => {
    try {
      const { data } = await getProductHubSummary(productId)
      return data?.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductHubList = createAsyncThunk(
  `${sliceName}/fetchProductHubList`,
  async (
    { productId, params }: { productId: number; params?: HubListParamsType; append?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await getProductHubList(productId, params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchSkuReviewHubDistribution = createAsyncThunk(
  `${sliceName}/fetchSkuReviewHubDistribution`,
  async (
    {
      productId,
      params,
      successCallback,
    }: {
      productId: number
      params: SkuReviewHubDistributionParamsType
      successCallback?: () => void
      append?: boolean
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getSkuReviewHubDistribution(productId, params)
      successCallback?.()
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchSkuReviewSummary = createAsyncThunk(
  `${sliceName}/fetchSkuReviewSummary`,
  async ({ params }: { params?: ForecastProductListParamsType }, { rejectWithValue }) => {
    try {
      const response = await getSkuReviewSummary(params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)
